import { useState } from "react";
import { siteInfo } from "@/mocks/data";
import Breadcrumb from "@/components/base/Breadcrumb";

const FORM_SUBMIT_URL = "https://readdy.ai/api/form/d9mu22gbdkpk99j7ge4g";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"success" | "error" | null>(null);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const websiteAlt = formData.get("website_alt") as string;
    if (websiteAlt && websiteAlt.trim()) {
      setFormStatus("success");
      return;
    }
    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== "website_alt") {
          params.append(key, value as string);
        }
      });
      const response = await fetch(FORM_SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  const teamMembers = [
    { name: "张明远", role: "创始人 & CEO", desc: "前知名视频平台技术总监，15年视频技术经验" },
    { name: "李思涵", role: "首席技术官", desc: "前阿里巴巴视频云架构师，专注流媒体技术" },
    { name: "王浩然", role: "内容运营总监", desc: "前爱奇艺内容运营负责人，10年影视行业经验" },
    { name: "陈小薇", role: "产品设计总监", desc: "前腾讯视频UX负责人，用户体验设计专家" },
  ];

  const milestones = [
    { year: "2019", title: "久久视频成立", desc: "三位热爱电影的工程师在北京创立久久视频，致力于打造高品质影视聚合平台" },
    { year: "2020", title: "用户突破100万", desc: "凭借蓝光画质和流畅体验，注册用户快速突破100万" },
    { year: "2021", title: "片源聚合升级", desc: "成功接入西瓜视频、樱花视频、糖豆视频等主流平台片源" },
    { year: "2022", title: "用户突破500万", desc: "月活跃用户超过500万，成为国内领先的免费影视平台" },
    { year: "2024", title: "4K蓝光全面覆盖", desc: "实现全平台4K蓝光画质支持，观影体验再升级" },
    { year: "2026", title: "开启新篇章", desc: "用户突破2000万，持续为用户提供最佳观影体验" },
  ];

  const testimonials = [
    { name: "王**", text: "画质真的没话说，蓝光4K在家看电影跟在电影院一样。关键是免费的！", role: "资深影迷" },
    { name: "李**", text: "用了三年了，片源越来越全。最新上映的电影基本都能找到蓝光版本。", role: "忠实用户" },
    { name: "张**", text: "家里老人喜欢看戏曲和纪录片，久久视频上有很多这类资源，非常好用。", role: "家庭用户" },
    { name: "刘**", text: "界面简洁没有广告，片源更新速度快。是朋友推荐给我的，现在我也推荐给朋友。", role: "上班族" },
  ];

  return (
    <main className="min-h-screen pt-24 pb-14">
      <section className="px-4 md:px-6 mb-12">
        <div className="max-w-[1200px] mx-auto">
          <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />
          <div className="mt-6 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              关于久久视频
            </h1>
            <p className="text-base text-foreground-600 max-w-3xl mx-auto leading-relaxed">
              {siteInfo.description}
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-14 px-4 md:px-6 bg-background-100/70">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-center text-foreground-900 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
            品牌故事
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-10 max-w-2xl mx-auto">
            从一个小小的想法到千万用户信赖的平台
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {milestones.map((m) => (
              <div key={m.year} className="p-5 rounded-lg bg-background-50 border border-background-200">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-3">
                  {m.year}
                </span>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1.5">{m.title}</h4>
                <p className="text-xs text-foreground-600 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-14 px-4 md:px-6">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-center text-foreground-900 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
            核心团队
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-10">一群热爱影视的技术人</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {teamMembers.map((member) => (
              <div key={member.name} className="p-5 rounded-lg bg-background-50 border border-background-200 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-4">
                  <span className="text-primary-600 text-xl font-bold">{member.name[0]}</span>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-0.5">{member.name}</h4>
                <p className="text-xs text-primary-600 font-medium mb-2">{member.role}</p>
                <p className="text-xs text-foreground-500 leading-relaxed">{member.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-14 px-4 md:px-6 bg-background-100/70">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-center text-foreground-900 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
            用户口碑
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-10">来自真实用户的声音</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {testimonials.map((t) => (
              <div key={t.name} className="p-5 rounded-lg bg-background-50 border border-background-200">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-amber-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-3">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                    <span className="text-secondary-600 text-xs font-bold">{t.name[0]}</span>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-14 px-4 md:px-6">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-center text-foreground-900 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
            联系我们
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-10">有任何问题或建议，欢迎随时联系我们</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 mb-10">
            <div className="p-5 rounded-lg bg-background-50 border border-background-200 text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                <i className="ri-mail-line text-xl"></i>
              </div>
              <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
              <p className="text-xs text-foreground-600">{siteInfo.email}</p>
            </div>
            <div className="p-5 rounded-lg bg-background-50 border border-background-200 text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                <i className="ri-phone-line text-xl"></i>
              </div>
              <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h4>
              <p className="text-xs text-foreground-600">{siteInfo.phone}</p>
            </div>
            <div className="p-5 rounded-lg bg-background-50 border border-background-200 text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-3">
                <i className="ri-map-pin-line text-xl"></i>
              </div>
              <h4 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
              <p className="text-xs text-foreground-600">{siteInfo.address}</p>
            </div>
          </div>

          <div className="p-6 rounded-lg bg-background-50 border border-background-200">
            <h3 className="text-base font-semibold text-foreground-900 mb-4 text-center">发送消息</h3>
            <form className="space-y-4" data-readdy-form="" onSubmit={handleContactSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact_name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    id="contact_name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 text-foreground-900"
                    placeholder="请输入姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact_email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    id="contact_email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 text-foreground-900"
                    placeholder="请输入邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact_subject" className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
                <input
                  id="contact_subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 text-foreground-900"
                  placeholder="请输入主题"
                />
              </div>
              <div>
                <label htmlFor="contact_message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                <textarea
                  id="contact_message"
                  name="message"
                  rows={4}
                  required
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 text-foreground-900 resize-none"
                  placeholder="请输入留言内容（不超过500字）"
                />
                <p className="text-xs text-foreground-400 mt-1 text-right">0/500</p>
              </div>
              <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              {formStatus && (
                <div className={`text-sm p-3 rounded-lg ${formStatus === 'success' ? 'bg-accent-50 text-accent-700' : 'bg-primary-50 text-primary-700'}`}>
                  {formStatus === 'success' ? '留言已发送，我们会尽快回复您！' : '发送失败，请稍后重试。'}
                </div>
              )}
              <button
                type="submit"
                className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                发送留言
              </button>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}